import { getPluginVersion } from "./envManager";
import type { MergeStep } from "./ooxml-merge";
import type {
    AttemptedOoxmlPair,
    RecoveryDrift,
    RedlineFailure,
    WordErrorFields,
} from "./redline-failure";

interface RedlineFailureAttemptCommon {
    capturedAt: string;
    addinVersion: string;
    errorMessage: string;
    reviewId: string | null;
}

export type RedlineFailureAttempt = RedlineFailureAttemptCommon & (
    | {
        stage: "range_unresolved";
        bookmarks: string[];
        attemptedOoxml: string;
    }
    | {
        stage: "bookmark_range_null";
        bookmarks: string[];
        attemptedOoxml: string;
    }
    | {
        stage: "invalid_bookmark_name";
        bookmarks: string[];
        invalidBookmarks: string[];
        attemptedOoxml: string;
    }
    | {
        stage: "untracked_write_detected";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        lostParagraphCount: number;
        addedParagraphCount: number;
    }
    | {
        stage: "merge_failed";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
        mergeStep: MergeStep;
    }
    | {
        stage: "backend_repair_exhausted";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
        repairAttempts: number;
        repairFailureMessage: string;
    }
    | {
        stage: "agent_recovery_threw";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
        agentFailureMessage: string;
    }
    | {
        stage: "agent_recovery_drifted";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
        drift: RecoveryDrift;
    }
    | {
        stage: "execute_js_failed";
        script: string;
        stdout: string;
        scriptError: string;
    }
);

export interface BuildRedlineFailureAttemptArgs {
    failure: RedlineFailure;
    error: Error;
    reviewId: string | null;
}

export function buildRedlineFailureAttempt({
    failure,
    error,
    reviewId,
}: BuildRedlineFailureAttemptArgs): RedlineFailureAttempt {
    const common: RedlineFailureAttemptCommon = {
        capturedAt: new Date().toISOString(),
        addinVersion: getPluginVersion(),
        errorMessage: error.message,
        reviewId,
    };
    switch (failure.stage) {
        case "range_unresolved":
            return {
                ...common,
                stage: "range_unresolved",
                bookmarks: failure.bookmarks,
                attemptedOoxml: failure.attemptedOoxml,
            };
        case "bookmark_range_null":
            return {
                ...common,
                stage: "bookmark_range_null",
                bookmarks: failure.bookmarks,
                attemptedOoxml: failure.attemptedOoxml,
            };
        case "invalid_bookmark_name":
            return {
                ...common,
                stage: "invalid_bookmark_name",
                bookmarks: failure.bookmarks,
                invalidBookmarks: failure.invalidBookmarks,
                attemptedOoxml: failure.attemptedOoxml,
            };
        case "untracked_write_detected":
            return {
                ...common,
                stage: "untracked_write_detected",
                bookmarks: failure.bookmarks,
                ooxml: failure.ooxml,
                lostParagraphCount: failure.lostParagraphs,
                addedParagraphCount: failure.addedParagraphs,
            };
        case "merge_failed":
            return {
                ...common,
                stage: "merge_failed",
                bookmarks: failure.bookmarks,
                ooxml: failure.ooxml,
                wordError: failure.wordError,
                mergeStep: failure.mergeStep,
            };
        case "backend_repair_exhausted":
            return {
                ...common,
                stage: "backend_repair_exhausted",
                bookmarks: failure.bookmarks,
                ooxml: failure.ooxml,
                wordError: failure.wordError,
                repairAttempts: failure.repairAttempts,
                repairFailureMessage: failure.repairFailureMessage,
            };
        case "agent_recovery_threw":
            return {
                ...common,
                stage: "agent_recovery_threw",
                bookmarks: failure.bookmarks,
                ooxml: failure.ooxml,
                wordError: failure.wordError,
                agentFailureMessage: failure.agentFailureMessage,
            };
        case "agent_recovery_drifted":
            return {
                ...common,
                stage: "agent_recovery_drifted",
                bookmarks: failure.bookmarks,
                ooxml: failure.ooxml,
                wordError: failure.wordError,
                drift: failure.drift,
            };
        case "execute_js_failed":
            return {
                ...common,
                stage: "execute_js_failed",
                script: failure.script,
                stdout: failure.stdout,
                scriptError: failure.scriptError,
            };
    }
}
